import { track } from "@/lib/analytics";
import { getGuardianId } from "@/lib/guardian";

// Attempt client for Simulation #2 only. session_id == attempt id (Phase B decision).
// Client-side persistence = sessionStorage (survives refresh/interruption, not cross-device).
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "adaptience_sim2_session";

const post = (path, body) =>
  fetch(`${API}${path}`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });

export const readStoredSession = () => { try { return sessionStorage.getItem(KEY); } catch { return null; } };
export const storeSession = (sid) => { try { sessionStorage.setItem(KEY, sid); } catch {} };
export const clearStoredSession = () => { try { sessionStorage.removeItem(KEY); } catch {} };

// Every genuine attempt (first Enter or Practise Again) = exactly one canonical sim_start.
// Replay additionally emits simulation_restart (Sim #1 semantics); it never replaces the start.
export async function startAttempt(sim, isReplay = false) {
  const sid = crypto.randomUUID();
  track("sim_start", null, { simulation_id: sim.simulationId, session_id: sid });
  if (isReplay) track("simulation_restart", null, { simulation_id: sim.simulationId, session_id: sid });
  await post("/simulation/start", { session_id: sid, simulation_id: sim.simulationId, slug: sim.slug, guardian_id: getGuardianId() }).catch(() => {});
  storeSession(sid);
  await appendEvent(sid, "attempt_started", 1);
  return sid;
}

export async function loadAttempt(sid) {
  const r = await fetch(`${API}/simulation/state/${encodeURIComponent(sid)}`, { credentials: "include" });
  if (!r.ok) return null;
  return r.json();
}

export const saveState = (sid, patch) => post("/simulation/state", { session_id: sid, state: patch }).catch(() => {});

// Idempotent by event_id; deterministic ids so retries/re-renders never duplicate history.
export const appendEvent = (sid, name, screen, suffix = "") =>
  post("/simulation/event", { session_id: sid, event_id: `${sid.slice(0, 8)}-${name}-${screen}${suffix}`.slice(0, 64), name, screen }).catch(() => {});

export const trackStage = (sim, sid, stage) =>
  track("simulation_progress", stage, { simulation_id: sim.simulationId, session_id: sid });

// Phase D: bounded classification. Text is sent transiently; only structured labels come back.
// Any failure degrades to the authored neutral family (never a new branch).
export async function classify(sid, screen, context, text) {
  try {
    const r = await post("/simulation/classify", { session_id: sid, screen, context, text });
    if (!r.ok) return { intent: "NEUTRAL_ENGAGE", source: "fallback" };
    return await r.json();
  } catch {
    return { intent: "NEUTRAL_ENGAGE", source: "fallback" };
  }
}

// Phase F4 — completion transaction pieces. Both idempotent server-side (record: $ifNull completion
// fields; build: create-once by session_id, emits simulation_complete only on first insert).
export const recordCompletion = (sid, sim) =>
  post("/simulation/record", { guardian_id: getGuardianId(), session_id: sid, simulation_id: sim.simulationId, slug: sim.slug, capabilities: [] });

export async function buildReport(sid) {
  const r = await post("/simulation/report/build", { session_id: sid });
  if (!r.ok) throw new Error(`build ${r.status}`);
  return r.json();
}
